import React, {useState, useEffect, useRef, useMemo} from 'react';
import {r} from "@/utils/responsive";
import {useProgress} from "@/hooks/useProgress";
import {useNav} from "@/hooks/useNav";
import {motion, AnimatePresence} from 'motion/react'
import {TRANSITIONS} from "@/config/animations";
import rewardBg from "@/assets/img/reward_bg.png"
import DisabledWrapper from "@/components/DisabledWrapper";
import {MaskShape, useMaskShapes} from "@/hooks/useMaskShapes";
import bgCardVideo from "@/assets/video/bg_card_video.mp4";
import ValidationErrorModal from "@/components/ValidationErrorModal";
import ButtonReforge from "@/components/ButtonReforge";
import ButtonSpan from "@/components/ButtonSpan";
import ShortcutText from "@/components/ShortcutText";
import TaskIcon from "@/components/TaskIcon";
import {Task} from "@/server/types";
import OracleAvatar from "@/components/OracleAvatar";
import ShortcutReforge from "@/components/ShortcutReforge";
import {useShortcuts} from "@/hooks/useShortcuts";
import {S3_FILES} from "@/config/s3_files";
import ReactPlayerWrapper from "@/components/ReactPlayerWrapper";
import {getModifierKey} from "@/utils/platform";

interface TaskPanelProps extends React.HTMLAttributes<HTMLDivElement> {
}

const TaskPanel: React.FC<TaskPanelProps> = ({className}) => {
    const {
        currentTask,
        currentStep,
        isLoading,
        currentYaml,
        submitSolution,
        validationResult,
        errorCount
    } = useProgress();
    const {currentSection, navigateToSection} = useNav();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

    const panelRef = useRef<HTMLDivElement>(null);


    // Создаем уникальный ID для маски панели
    const maskId = useMemo(() => `task-panel-mask-${Math.random().toString(36).substring(2, 11)}`, []);

    const taskPanelMask: MaskShape[] = useMemo(() => [
        {
            width: currentSection === 'briefing' ? 644 : 577,
            height: 100,
            path: currentSection === 'briefing' ? "M104.479 0C112.215 0 116.084 0.000160993 119.858 0.732422C123.632 1.46472 127.22 2.9115 134.396 5.80469L160.604 16.3721C167.78 19.2653 171.368 20.712 175.142 21.4443C178.916 22.1766 182.785 22.1768 190.521 22.1768H457.919C464.445 22.1768 467.708 22.1766 470.914 21.6523C474.12 21.1281 477.214 20.0894 483.4 18.0107L491.6 15.2549C497.786 13.1762 500.88 12.1366 504.086 11.6123C507.292 11.0881 510.555 11.0889 517.081 11.0889H564C601.712 11.0889 620.568 11.089 632.284 22.8047C644 34.5204 644 53.3767 644 91.0889V100H0V80C0 42.2876 9.156e-05 23.4315 11.7158 11.7158C23.4315 9.18069e-05 42.2876 0 80 0H104.479Z" : "M92.709 0C106.229 1.30406e-10 112.99 0.000312805 119.27 2.14258C125.549 4.2849 130.9 8.4167 141.602 16.6797L153.398 25.7881C164.1 34.0511 169.451 38.1829 175.73 40.3252C182.01 42.4675 188.771 42.4678 202.291 42.4678H375.902C389.815 42.4678 396.771 42.468 403.206 40.208C409.641 37.948 415.07 33.598 425.927 24.8984L435.073 17.5693C445.93 8.86977 451.359 4.51979 457.794 2.25977C464.229 -0.000185966 471.185 0 485.098 0H497C534.712 0 553.568 9.15527e-05 565.284 11.7158C577 23.4315 577 42.2876 577 80V99H0V80C0 42.2876 9.15527e-05 23.4315 11.7158 11.7158C23.4315 9.15527e-05 42.2876 0 80 0H92.709Z",
            position: "top"
        },
        {
            position: "middle",
            fluid: true,
        },
        {
            width: currentSection === 'briefing' ? 644 : 577,
            height: 100,
            path: currentSection === 'briefing' ? "M644 7.60547C644 45.3177 644 64.1739 632.284 75.8896C620.568 87.6053 601.712 87.6055 564 87.6055H550.536C540.78 87.6055 535.902 87.6056 531.072 88.1953C526.242 88.7851 521.507 89.9583 512.038 92.3057L499.962 95.2998C490.493 97.6472 485.758 98.8204 480.928 99.4102C476.098 99.9999 471.22 100 461.464 100H80C42.2876 100 23.4315 99.9999 11.7158 88.2842C9.15527e-05 76.5685 0 57.7124 0 20V0H644V7.60547Z" : "M577 8.00879C577 45.7209 577 64.5772 565.284 76.293C553.568 88.0087 534.712 88.0088 497 88.0088H492.476C487.245 88.0088 484.63 88.0091 482.044 88.3477C479.458 88.6863 476.931 89.3594 471.877 90.7061L447.123 97.3027C442.069 98.6494 439.542 99.3225 436.956 99.6611C434.37 99.9997 431.755 100 426.524 100H80C42.2876 100 23.4315 99.9999 11.7158 88.2842C9.53674e-05 76.5685 0 57.7124 0 20V0H577V8.00879Z",
            position: "bottom"
        }
    ], [currentSection])

    const taskPanelTopMask: MaskShape[] = useMemo(() => [
        {
            width: currentSection === 'briefing' ? 644 : 577,
            height: 214,
            position: "top",
            path: "M97.8861 171.5H79.6049C42.2769 171.5 23.613 171.5 11.936 160.003C11.7886 159.858 11.6423 159.711 11.4971 159.564C0 147.887 0 129.223 0 91.8951C0 66.0388 0 53.1107 8.56607 42.2255C8.66302 42.1023 8.79935 41.9314 8.89793 41.8095C17.6081 31.0392 28.9827 28.4474 51.7321 23.2638C98.6195 12.5801 177.59 0 289.5 0C401.025 0 479.179 12.4936 525.564 23.1533C548.21 28.3575 559.533 30.9597 568.233 41.8347C568.268 41.8776 568.316 41.9381 568.35 41.981C577 52.8967 577 65.8108 577 91.639C577 129.216 577 148.005 565.361 159.707C565.31 159.759 565.259 159.81 565.207 159.861C553.505 171.5 534.716 171.5 497.139 171.5H479.964C465.631 171.5 458.464 171.5 451.865 173.889C445.266 176.278 439.76 180.866 428.749 190.042L422.251 195.458C411.24 204.634 405.734 209.222 399.135 211.611C392.536 214 385.369 214 371.036 214H207.114C193.194 214 186.233 214 179.795 211.738C173.357 209.475 167.927 205.121 157.067 196.412L157.067 196.412L147.933 189.088C137.073 180.379 131.643 176.025 125.205 173.762C118.767 171.5 111.807 171.5 97.8861 171.5Z"
        }
    ], [currentSection])

    // Получаем SVG элемент с масками используя svg.js
    const {
        svgJSX,
        clipPath,
        strokeSVG
    } = useMaskShapes(taskPanelMask, maskId, panelRef, true, 8);

    // Получаем SVG элемент для верхней маски
    const topMaskRef = useRef<HTMLDivElement>(null);
    const {
        svgJSX: topMaskSvgJSX
    } = useMaskShapes(taskPanelTopMask, `${maskId}-top`, topMaskRef);

    // Показываем модалку с ошибкой при неуспешной валидации
    useEffect(() => {
        if (validationResult && !validationResult.success && validationResult.errors) {
            setIsErrorModalOpen(true);
        }
    }, [validationResult]);

    const handleEnterAction = async () => {
        if (currentSection === 'briefing') {
            navigateToSection('editor');
        }
        // В редакторе ENTER не должен отправлять задачу
    };

    const handleCtrlF = () => {
        // Открываем поиск в редакторе Monaco
        const monacoEditor = document.querySelector('.monaco-editor');
        if (monacoEditor) {
            const event = new KeyboardEvent('keydown', {
                key: 'f',
                ctrlKey: true,
                bubbles: true
            });
            monacoEditor.dispatchEvent(event);
        }
    };

    useShortcuts({
        onEnter: currentSection === 'briefing' ? handleEnterAction : undefined,
        onCtrlEnter: currentSection === 'editor' ? async () => {
            if (currentYaml && currentTask && !isLoading) {
                await submitSolution(currentYaml);
            }
        } : undefined,
        enabledSections: ['briefing', 'editor']
    });

    // Используем рефы для хранения актуальных значений
    const submitRef = React.useRef({currentYaml, currentTask, isLoading, submitSolution});
    React.useEffect(() => {
        submitRef.current = {currentYaml, currentTask, isLoading, submitSolution};
    }, [currentYaml, currentTask, isLoading, submitSolution]);

    // Резервный обработчик для Ctrl+Enter
    React.useEffect(() => {
        if (currentSection !== 'editor') return;

        const handleKeyDown = async (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                const {currentYaml, currentTask, isLoading, submitSolution} = submitRef.current;
                e.preventDefault();
                if (currentYaml && currentTask && !isLoading) {
                    await submitSolution(currentYaml);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentSection]);

    const renderContent = () => {
        if (!currentTask) return null;

        return (
            <>
                {/* На секции брифинга показываем ситуацию */}
                {currentSection === 'briefing' && currentTask.situation && (
                    Array.isArray(currentTask.situation) ? (
                        currentTask.situation.map((item, index) => (
                            <p key={index}>{item}</p>
                        ))
                    ) : (
                        <p>{currentTask.situation}</p>
                    )
                )}
                {/* На секции редактора показываем подсказку */}
                {currentSection === 'editor' && currentTask.description && (
                    Array.isArray(currentTask.description) ? (
                        currentTask.description.map((item, index) => (
                            <div key={index} style={{
                                display: 'flex',
                                alignItems: 'start',
                                gap: r.vmax(16),
                                fontSize: r.vmax(24),
                                lineHeight: r.vmax(32),
                            }}>
                                <div style={{
                                    paddingLeft: r.vmax(0),
                                    paddingTop: r.vmax(9),
                                }}>
                                    <svg style={{
                                        width: r.vmax(12),
                                        height: r.vmax(14),
                                    }} width="12" height="14" viewBox="0 0 12 14" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M0 1.92V0H2.88V1.92H0ZM2.88 3.84V1.92H5.76V3.84H2.88ZM5.76 5.76V3.84H8.64V5.76H5.76ZM8.64 7.68V5.76H11.52V7.68H8.64ZM8.64 7.68V9.6H5.76V7.68H8.64ZM5.76 9.6V11.52H2.88V9.6H5.76ZM0 13.44V11.52H2.88V13.44H0Z"
                                            fill="white"/>
                                    </svg>
                                </div>
                                <div>
                                    <p>{item}</p>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p>{currentTask.hint}</p>
                    )
                )}
            </>
        );
    };

    return (
        <>
            <motion.div
                style={{
                    position: 'absolute',
                    width: currentSection === 'briefing' ? r.vmax(644) : r.vmax(577),
                    display: 'flex',
                    flexDirection: 'column',
                    gap: r.vmax(50)
                }}
                initial={{
                    top: currentSection === 'briefing'
                        ? (currentStep === 4 ? r.vmax(108) : r.vmax(136))
                        : r.vmax(250),
                    right: currentSection === 'briefing' ? r.vmax(164) : r.vmax(50),
                }}
                animate={{
                    top: currentSection === 'briefing'
                        ? (currentStep === 4 ? r.vmax(108) : r.vmax(136))
                        : r.vmax(250),
                    right: currentSection === 'briefing' ? r.vmax(164) : r.vmax(50),
                }}
                transition={TRANSITIONS.slow}
            >
                <div
                    ref={topMaskRef}
                    style={{
                        position: 'absolute',
                        top: r.vmax(-178),
                        left: '50%',
                        transform: 'translateX(-50%)',
                        // background: '#0C102B',
                        // background: 'red',
                        width: r.vmax(currentSection === 'briefing' ? 644 : 577),
                        height: r.vmax(214),
                        // boxShadow: 'inset 0px 5px 50px #FFFFFF, inset 0px -26px 58px #5282FF, inset 0px -23.8033px 19.9947px #1741B1',
                        // clipPath: currentSection === 'editor' ? `url(#${maskId}-top)` : 'none',
                        display: currentSection === 'editor' ? 'block' : 'none'
                    }}>
                    <div style={{
                        position: 'absolute',
                        top: r.vmax(-50),
                        left: '50%',
                        transform: 'translateX(-50%)',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}>
                        <svg style={{
                            width: r.vmax(725),
                            height: r.vmax(362),
                        }} viewBox="0 0 725 362" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g filter="url(#filter0_d_454_59177)">
                                <mask id="mask0_454_59177" style={{
                                    maskType: 'alpha'
                                }} maskUnits="userSpaceOnUse" x="74" y="50" width="577" height="214">
                                    <path
                                        d="M171.886 221.5H152.457C116.244 221.5 98.137 221.5 86.5855 210.628C85.9971 210.074 85.4257 209.503 84.8719 208.915C74 197.363 74 179.256 74 143.043C74 117.05 74 104.053 82.158 93.3232C82.5389 92.8222 83.0571 92.1764 83.4635 91.6959C92.1699 81.4058 103.797 78.8239 127.05 73.6601C175.531 62.8938 256.028 50 363.5 50C470.601 50 550.238 62.8049 598.207 73.5484C621.372 78.7366 632.955 81.3307 641.654 91.707C642 92.1204 642.448 92.6798 642.776 93.1084C651 103.864 651 116.853 651 142.83C651 179.25 651 197.46 640.013 209.036C639.534 209.541 639.041 210.034 638.536 210.513C626.96 221.5 608.75 221.5 572.33 221.5H553.964C539.631 221.5 532.464 221.5 525.865 223.889C519.266 226.278 513.76 230.866 502.749 240.042L496.251 245.458C485.24 254.634 479.734 259.222 473.135 261.611C466.536 264 459.369 264 445.036 264H281.114C267.194 264 260.233 264 253.795 261.738C247.357 259.475 241.927 255.121 231.067 246.412L231.067 246.412L221.933 239.088C211.073 230.379 205.643 226.025 199.205 223.762C192.767 221.5 185.807 221.5 171.886 221.5Z"
                                        fill="#0C102B"/>
                                </mask>
                                <g mask="url(#mask0_454_59177)">
                                    <g filter="url(#filter1_iii_454_59177)">
                                        <path
                                            d="M171.886 221.5H153.605C116.277 221.5 97.613 221.5 85.936 210.003C85.7886 209.858 85.6423 209.711 85.4971 209.564C74 197.887 74 179.223 74 141.895C74 116.039 74 103.111 82.5661 92.2255C82.663 92.1023 82.7993 91.9314 82.8979 91.8095C91.6081 81.0392 102.983 78.4474 125.732 73.2638C172.62 62.5801 251.59 50 363.5 50C475.025 50 553.179 62.4936 599.564 73.1533C622.21 78.3575 633.533 80.9597 642.233 91.8347C642.268 91.8776 642.316 91.9381 642.35 91.981C651 102.897 651 115.811 651 141.639C651 179.216 651 198.005 639.361 209.707C639.31 209.759 639.259 209.81 639.207 209.861C627.505 221.5 608.716 221.5 571.139 221.5H553.964C539.631 221.5 532.464 221.5 525.865 223.889C519.266 226.278 513.76 230.866 502.749 240.042L496.251 245.458C485.24 254.634 479.734 259.222 473.135 261.611C466.536 264 459.369 264 445.036 264H281.114C267.194 264 260.233 264 253.795 261.738C247.357 259.475 241.927 255.121 231.067 246.412L231.067 246.412L221.933 239.088C211.073 230.379 205.643 226.025 199.205 223.762C192.767 221.5 185.807 221.5 171.886 221.5Z"
                                            fill="#0C102B"/>
                                    </g>
                                    <path
                                        d="M56 34L381.311 151.838M381.311 151.838L381.412 151.875M381.311 151.838L381.36 151.72M381.311 151.838L381.274 151.925M381.412 151.875L381.5 151.907M381.412 151.875L381.494 151.814M381.412 151.875L381.332 151.934M381.5 151.907L381.603 151.944M381.5 151.907L381.583 151.877M381.5 151.907L381.397 151.944M381.603 151.944L381.757 152M381.603 151.944L381.665 151.935M381.603 151.944L381.5 151.96M381.757 152L381.665 151.935M381.757 152L381.726 151.925M381.757 152L381.5 151.96M381.757 152H381.243M381.757 152H707M194 19.5L381.36 151.72M381.36 151.72L381.494 151.814M381.36 151.72L381.5 151.387M381.494 151.814L381.583 151.877M381.494 151.814L381.635 151.709M381.583 151.877L381.665 151.935M381.583 151.877L381.689 151.838M381.665 151.935L381.726 151.925M333.897 38L381.5 151.387M381.5 151.387L381.635 151.709M381.5 151.387L429.103 38M381.635 151.709L381.689 151.838M381.635 151.709L560 19.5M381.689 151.838L381.726 151.925M381.689 151.838L707 34M381.726 151.925L707 101.5M56 101.5L381.274 151.925M381.274 151.925L381.332 151.934M381.274 151.925L381.243 152M381.332 151.934L381.397 151.944M381.332 151.934L381.243 152M381.397 151.944L381.5 151.96M381.397 151.944L381.243 152M381.5 151.96L381.243 152M56 152H381.243M56 270.5L381.311 152.662M381.311 152.662L381.412 152.625M381.311 152.662L381.36 152.78M381.311 152.662L381.274 152.575M381.412 152.625L381.5 152.593M381.412 152.625L381.494 152.686M381.412 152.625L381.332 152.566M381.5 152.593L381.603 152.556M381.5 152.593L381.583 152.623M381.5 152.593L381.397 152.556M381.603 152.556L381.757 152.5M381.603 152.556L381.665 152.565M381.603 152.556L381.5 152.54M381.757 152.5L381.665 152.565M381.757 152.5L381.726 152.575M381.757 152.5L381.5 152.54M381.757 152.5H381.243M381.757 152.5H707M194 285L381.36 152.78M381.36 152.78L381.494 152.686M381.36 152.78L381.5 153.113M381.494 152.686L381.583 152.623M381.494 152.686L381.635 152.791M381.583 152.623L381.665 152.565M381.583 152.623L381.689 152.662M381.665 152.565L381.726 152.575M333.897 266.5L381.5 153.113M381.5 153.113L381.635 152.791M381.5 153.113L429.103 266.5M381.635 152.791L381.689 152.662M381.635 152.791L560 285M381.689 152.662L381.726 152.575M381.689 152.662L707 270.5M381.726 152.575L707 203M56 203L381.274 152.575M381.274 152.575L381.332 152.566M381.274 152.575L381.243 152.5M381.332 152.566L381.397 152.556M381.332 152.566L381.243 152.5M381.397 152.556L381.5 152.54M381.397 152.556L381.243 152.5M381.5 152.54L381.243 152.5M56 152.5H381.243"
                                        stroke="#3344BC" stroke-width="2" stroke-dasharray="16 16"
                                        style={{
                                            mixBlendMode: 'color-dodge'
                                        }}/>
                                    <ellipse cx="382" cy="156.5" rx="141" ry="97.5"
                                             fill="url(#paint0_radial_454_59177)"/>
                                    <path
                                        d="M363.5 52C474.874 52 552.878 64.4766 599.116 75.1025C621.965 80.3534 632.491 82.8592 640.672 93.084C640.705 93.1249 640.75 93.1834 640.782 93.2236C644.829 98.3301 646.891 103.922 647.938 111.491C648.996 119.145 649 128.671 649 141.639C649 160.484 648.995 174.417 647.562 185.11C646.137 195.747 643.332 202.879 637.943 208.297C637.895 208.345 637.846 208.394 637.796 208.443C632.378 213.832 625.246 216.637 614.61 218.062C603.917 219.495 589.984 219.5 571.139 219.5H553.964C539.772 219.5 532.19 219.473 525.185 222.009C518.179 224.545 512.372 229.42 501.469 238.506L494.971 243.921C483.851 253.187 478.647 257.488 472.454 259.73C466.261 261.972 459.51 262 445.036 262H281.114C267.057 262 260.5 261.974 254.458 259.851C249.172 257.993 244.582 254.635 236.171 247.934L232.318 244.852L223.185 237.527C212.431 228.904 206.702 224.278 199.868 221.876C193.034 219.474 185.67 219.5 171.886 219.5H153.604C134.885 219.5 121.044 219.496 110.415 218.08C99.8427 216.672 92.7446 213.901 87.3389 208.578C87.1988 208.44 87.0597 208.301 86.9219 208.161C81.5994 202.755 78.8279 195.657 77.4199 185.085C76.0045 174.456 76 160.615 76 141.896C76 128.914 76.0048 119.376 77.0527 111.718C78.0895 104.141 80.1318 98.5523 84.1377 93.4619C84.2291 93.3457 84.3602 93.1823 84.4531 93.0674C92.6417 82.942 103.221 80.4446 126.177 75.2139C172.92 64.563 251.741 52 363.5 52Z"
                                        stroke="url(#paint1_linear_454_59177)" stroke-width="4"/>
                                    <path
                                        d="M360.5 54C472.451 54 551.684 66.6064 598.681 77.2695C621.689 82.4899 632.293 84.9828 640.485 95.0596C640.608 95.211 640.78 95.4258 640.9 95.5791C644.886 100.661 646.92 106.248 647.952 113.827C648.995 121.488 649 131.032 649 144.02C649 162.679 648.996 176.475 647.589 187.072C646.189 197.614 643.433 204.695 638.143 210.095C637.962 210.279 637.779 210.462 637.595 210.643C632.195 215.933 625.114 218.689 614.572 220.089C603.975 221.496 590.179 221.5 571.52 221.5H550.599C538.027 221.5 531.312 221.478 524.999 223.498C518.687 225.518 513.232 229.434 502.996 236.731L502.995 236.732L490.683 245.511H490.682C480.244 252.952 475.362 256.407 469.781 258.192C464.2 259.978 458.22 260 445.401 260H278.99C265.978 260 259.908 259.977 254.254 258.141C248.6 256.304 243.676 252.755 233.148 245.107L222.203 237.156C211.879 229.657 206.378 225.632 199.982 223.555C193.587 221.477 186.771 221.5 174.011 221.5H153.993C135.084 221.5 121.102 221.496 110.376 220.054C99.7073 218.62 92.5577 215.796 87.1338 210.374L87.125 210.365C81.7033 204.941 78.8804 197.792 77.4463 187.124C76.0044 176.398 76 162.416 76 143.507C76 130.546 76.0041 121.027 77.0674 113.376C78.086 106.046 80.0622 100.567 83.8828 95.582L84.2588 95.1016L84.2646 95.0938C92.4414 84.8176 102.94 82.3077 125.734 77.0469C171.72 66.4335 249.319 54 360.5 54Z"
                                        stroke="url(#paint2_linear_454_59177)" stroke-width="4"/>
                                </g>
                            </g>
                            <defs>
                                <filter id="filter0_d_454_59177" x="0" y="0" width="725" height="362"
                                        filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix"
                                                   values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                                   result="hardAlpha"/>
                                    <feOffset dy="24"/>
                                    <feGaussianBlur stdDeviation="37"/>
                                    <feComposite in2="hardAlpha" operator="out"/>
                                    <feColorMatrix type="matrix"
                                                   values="0 0 0 0 0.321569 0 0 0 0 0.509804 0 0 0 0 1 0 0 0 1 0"/>
                                    <feBlend mode="plus-lighter" in2="BackgroundImageFix"
                                             result="effect1_dropShadow_454_59177"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_454_59177"
                                             result="shape"/>
                                </filter>
                                <filter id="filter1_iii_454_59177" x="74" y="24.1344" width="577" height="244.866"
                                        filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix"
                                                   values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                                   result="hardAlpha"/>
                                    <feMorphology radius="8.33114" operator="dilate" in="SourceAlpha"
                                                  result="effect1_innerShadow_454_59177"/>
                                    <feOffset dy="-23.8033"/>
                                    <feGaussianBlur stdDeviation="9.99737"/>
                                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                                    <feColorMatrix type="matrix"
                                                   values="0 0 0 0 0.0887131 0 0 0 0 0.256164 0 0 0 0 0.692233 0 0 0 1 0"/>
                                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_454_59177"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix"
                                                   values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                                   result="hardAlpha"/>
                                    <feMorphology radius="32.1344" operator="dilate" in="SourceAlpha"
                                                  result="effect2_innerShadow_454_59177"/>
                                    <feOffset dy="-26"/>
                                    <feGaussianBlur stdDeviation="29"/>
                                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                                    <feColorMatrix type="matrix"
                                                   values="0 0 0 0 0.321569 0 0 0 0 0.509804 0 0 0 0 1 0 0 0 1 0"/>
                                    <feBlend mode="color-dodge" in2="effect1_innerShadow_454_59177"
                                             result="effect2_innerShadow_454_59177"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix"
                                                   values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                                   result="hardAlpha"/>
                                    <feMorphology radius="4.76065" operator="dilate" in="SourceAlpha"
                                                  result="effect3_innerShadow_454_59177"/>
                                    <feOffset dy="5"/>
                                    <feGaussianBlur stdDeviation="25"/>
                                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0"/>
                                    <feBlend mode="plus-lighter" in2="effect2_innerShadow_454_59177"
                                             result="effect3_innerShadow_454_59177"/>
                                </filter>
                                <radialGradient id="paint0_radial_454_59177" cx="0" cy="0" r="1"
                                                gradientUnits="userSpaceOnUse"
                                                gradientTransform="translate(382 156.5) rotate(90) scale(97.5 141)">
                                    <stop offset="0.55" stop-color="#0C102C"/>
                                    <stop offset="1" stop-color="#0C102C" stop-opacity="0"/>
                                </radialGradient>
                                <linearGradient id="paint1_linear_454_59177" x1="372.5" y1="50" x2="372.5" y2="270.941"
                                                gradientUnits="userSpaceOnUse">
                                    <stop stop-color="#5282FF"/>
                                    <stop offset="0.25" stop-color="#F5F8FF"/>
                                    <stop offset="0.5" stop-color="#5282FF"/>
                                    <stop offset="0.75" stop-color="#C6FFFF"/>
                                    <stop offset="1" stop-color="#C6FFFF"/>
                                </linearGradient>
                                <linearGradient id="paint2_linear_454_59177" x1="372.5" y1="52" x2="372.5" y2="272.941"
                                                gradientUnits="userSpaceOnUse">
                                    <stop stop-color="#5282FF"/>
                                    <stop offset="0.25" stop-color="#F5F8FF"/>
                                    <stop offset="0.5" stop-color="#5282FF"/>
                                    <stop offset="0.75" stop-color="#C6FFFF"/>
                                    <stop offset="1" stop-color="#C6FFFF"/>
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        clipPath: `url(#${maskId}-top)`,
                        opacity: 0.7,
                        mixBlendMode: 'screen',
                    }}>
                        <OracleAvatar style={{
                            position: 'absolute',
                            top: '55%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            inset: 'initial',
                        }}/>
                    </div>
                    {/*<div style={{*/}
                    {/*    zIndex: 1,*/}
                    {/*    width: r.vmax(282),*/}
                    {/*    height: r.vmax(195),*/}
                    {/*    position: 'absolute',*/}
                    {/*    top: '50%',*/}
                    {/*    left: '50%',*/}
                    {/*    transform: 'translate(-50%, -50%)',*/}
                    {/*    background: 'radial-gradient(50% 50% at 50% 50%, #0C102C 55%, rgba(12, 16, 44, 0) 100%)',*/}
                    {/*}}>*/}
                    {/*</div>*/}
                    {/*<svg width={currentSection === 'briefing' ? "644" : "577"} height="214" style={{*/}
                    {/*    position: 'absolute',*/}
                    {/*    top: '50%',*/}
                    {/*    left: '50%',*/}
                    {/*    transform: 'translate(-50%, -50%)',*/}
                    {/*}} viewBox={`0 0 ${currentSection === 'briefing' ? 644 : 577} 214`} fill="none"*/}
                    {/*     xmlns="http://www.w3.org/2000/svg">*/}
                    {/*    <path*/}
                    {/*        d="M-18 -16L307.311 101.838M307.311 101.838L307.412 101.875M307.311 101.838L307.36 101.72M307.311 101.838L307.274 101.925M307.412 101.875L307.5 101.907M307.412 101.875L307.494 101.814M307.412 101.875L307.332 101.934M307.5 101.907L307.603 101.944M307.5 101.907L307.583 101.877M307.5 101.907L307.397 101.944M307.603 101.944L307.757 102M307.603 101.944L307.665 101.935M307.603 101.944L307.5 101.96M307.757 102L307.665 101.935M307.757 102L307.726 101.925M307.757 102L307.5 101.96M307.757 102H307.243M307.757 102H633M120 -30.5L307.36 101.72M307.36 101.72L307.494 101.814M307.36 101.72L307.5 101.387M307.494 101.814L307.583 101.877M307.494 101.814L307.635 101.709M307.583 101.877L307.665 101.935M307.583 101.877L307.689 101.838M307.665 101.935L307.726 101.925M259.897 -12L307.5 101.387M307.5 101.387L307.635 101.709M307.5 101.387L355.103 -12M307.635 101.709L307.689 101.838M307.635 101.709L486 -30.5M307.689 101.838L307.726 101.925M307.689 101.838L633 -16M307.726 101.925L633 51.5M-18 51.5L307.274 101.925M307.274 101.925L307.332 101.934M307.274 101.925L307.243 102M307.332 101.934L307.397 101.944M307.332 101.934L307.243 102M307.397 101.944L307.5 101.96M307.397 101.944L307.243 102M307.5 101.96L307.243 102M-18 102H307.243M-18 220.5L307.311 102.662M307.311 102.662L307.412 102.625M307.311 102.662L307.36 102.78M307.311 102.662L307.274 102.575M307.412 102.625L307.5 102.593M307.412 102.625L307.494 102.686M307.412 102.625L307.332 102.566M307.5 102.593L307.603 102.556M307.5 102.593L307.583 102.623M307.5 102.593L307.397 102.556M307.603 102.556L307.757 102.5M307.603 102.556L307.665 102.565M307.603 102.556L307.5 102.54M307.757 102.5L307.665 102.565M307.757 102.5L307.726 102.575M307.757 102.5L307.5 102.54M307.757 102.5H307.243M307.757 102.5H633M120 235L307.36 102.78M307.36 102.78L307.494 102.686M307.36 102.78L307.5 103.113M307.494 102.686L307.583 102.623M307.494 102.686L307.635 102.791M307.583 102.623L307.665 102.565M307.583 102.623L307.689 102.662M307.665 102.565L307.726 102.575M259.897 216.5L307.5 103.113M307.5 103.113L307.635 102.791M307.5 103.113L355.103 216.5M307.635 102.791L307.689 102.662M307.635 102.791L486 235M307.689 102.662L307.726 102.575M307.689 102.662L633 220.5M307.726 102.575L633 153M-18 153L307.274 102.575M307.274 102.575L307.332 102.566M307.274 102.575L307.243 102.5M307.332 102.566L307.397 102.556M307.332 102.566L307.243 102.5M307.397 102.556L307.5 102.54M307.397 102.556L307.243 102.5M307.5 102.54L307.243 102.5M-18 102.5H307.243"*/}
                    {/*        stroke="#3344BC" stroke-width="2" stroke-dasharray="16 16"*/}
                    {/*        style={{*/}
                    {/*            mixBlendMode: 'color-dodge'*/}
                    {/*        }}/>*/}
                    {/*</svg>*/}
                    <div style={{
                        position: 'absolute',
                        bottom: r.vmax(8),
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: r.vmax(18),
                        lineHeight: r.vmax(40),
                        color: "white",
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        zIndex: 8
                    }}>
                        <div className="animate-fade">
                            <svg
                                width="12"
                                height="15"
                                viewBox="0 0 12 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M0.159912 2.52001V0.600006H3.03991V2.52001H0.159912ZM3.03991 4.44001V2.52001H5.91991V4.44001H3.03991ZM5.91991 6.36001V4.44001H8.79991V6.36001H5.91991ZM8.79991 8.28001V6.36001H11.6799V8.28001H8.79991ZM8.79991 8.28001V10.2H5.91991V8.28001H8.79991ZM5.91991 10.2V12.12H3.03991V10.2H5.91991ZM0.159912 14.04V12.12H3.03991V14.04H0.159912Z"
                                    fill="white"/>
                            </svg>
                        </div>
                        <span style={{
                            letterSpacing: r.vmax(-0.60),
                        }}>{currentTask?.name}</span>
                        <div
                            className="animate-fade"
                            style={{
                                transform: 'rotate(180deg)'
                            }}
                        >
                            <svg
                                width="12"
                                height="15"
                                viewBox="0 0 12 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M0.159912 2.52001V0.600006H3.03991V2.52001H0.159912ZM3.03991 4.44001V2.52001H5.91991V4.44001H3.03991ZM5.91991 6.36001V4.44001H8.79991V6.36001H5.91991ZM8.79991 8.28001V6.36001H11.6799V8.28001H8.79991ZM8.79991 8.28001V10.2H5.91991V8.28001H8.79991ZM5.91991 10.2V12.12H3.03991V10.2H5.91991ZM0.159912 14.04V12.12H3.03991V14.04H0.159912Z"
                                    fill="white"/>
                            </svg>
                        </div>
                    </div>
                </div>
                {svgJSX}
                {topMaskSvgJSX}
                <div
                    ref={panelRef}
                    style={{
                        position: 'relative',
                        background: '#001F6150',
                        padding: r.vmax(20),
                        paddingTop: r.vmax(68),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: r.vmax(48),
                        width: currentSection === 'briefing' ? r.vmax(644) : r.vmax(577),
                        height: currentSection === 'briefing' ? r.vmax(currentStep === 4 ? 700 : currentStep === 5 ? 605 : 570) : `calc(${r.vmin(1080)} - ${r.vmax(250)} - ${r.vmax(168)})`,
                        clipPath: clipPath,
                        backdropFilter: 'blur(44px)',
                        transition: 'all 0.25s ease-in-out',
                        boxShadow: `inset 0px 5px 50px #FFFFFF, inset 0px -26px 58px #5282FF, inset 0px -23.8033px 19.9947px #1741B1`
                    }}>
                    <div style={{
                        paddingLeft: r.vmax(24),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: currentSection === 'briefing' ? r.vmax(24) : r.vmax(20),
                        fontSize: r.vmax(28),
                        lineHeight: r.vmax(36),
                        color: 'white',
                        whiteSpace: 'pre-wrap',
                        paddingTop: currentSection === "editor" ? r.vmax(20) : 0,
                        letterSpacing: r.vmax(-0.20),
                    }}>
                        {renderContent()}
                    </div>
                    <div style={{
                        width: '100%',
                        height: currentSection === 'briefing' ? r.vmax(121) : r.vmax(110)
                    }}></div>
                    <div style={{
                        position: 'absolute',
                        left: r.vmax(20),
                        bottom: r.vmax(20),
                        right: r.vmax(20),
                    }}>
                        <ButtonReforge height={currentSection === 'briefing' ? 121 : 110}
                                       minHeight={currentSection === 'briefing' ? 121 : 110}
                            // style={{
                            //     background: '#5282FF',
                            //     boxShadow: '0px 10px 40px #5588FF, inset 0px -26px 34px rgba(255, 255, 255, 0.25), inset 0px 5px 50px #FFFFFF, inset 0px -26px 58px #5282FF, inset 0px -23.8033px 19.9947px #1741B1'
                            // }}
                                       onClick={currentSection === 'briefing'
                                           ? () => navigateToSection('editor')
                                           : async () => {
                                               if (currentYaml && currentTask) {
                                                   await submitSolution(currentYaml);
                                                   // Логика перехода теперь обрабатывается в ValidationResult/AwardLayer
                                               }
                                           }
                                       }
                                       disabled={currentSection === 'editor' && (!currentYaml || isLoading)}
                        >
                            <ButtonSpan>
                                {currentSection === 'briefing'
                                    ? 'Перейти к задаче'
                                    : isLoading ? 'Проверяем...' : 'Проверить'
                                }
                            </ButtonSpan>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: r.vmax(4),
                            }}>
                                {currentSection === 'briefing' ? (
                                    // <ShortcutText text="ENTER"/>
                                    <ShortcutReforge isLarge>ENTER</ShortcutReforge>
                                ) : (
                                    <>
                                        <ShortcutReforge>{getModifierKey()}</ShortcutReforge>
                                        <ButtonSpan style={{
                                            fontSize: r.vmax(18),
                                            lineHeight: r.vmax(24),
                                        }}>+</ButtonSpan>
                                        <ShortcutReforge>ENTER</ShortcutReforge>
                                    </>
                                )}
                            </div>
                        </ButtonReforge>
                    </div>
                    <DisabledWrapper style={{zIndex: -1}}>
                        <ReactPlayerWrapper videoSrc={S3_FILES.CHARACTER_CARD_BG_VIDEO}
                                            posterSrc={S3_FILES.CHARACTER_CARD_BG_VIDEO_POSTER} style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            zIndex: -1,
                            mixBlendMode: 'color-dodge',
                            opacity: .5
                        }}/>
                    </DisabledWrapper>
                </div>
                <AnimatePresence mode="wait">
                    {currentSection === 'briefing' && (
                        <motion.div
                            initial={{opacity: 0, y: -20}}
                            animate={{opacity: 1, y: 0}}
                            exit={{opacity: 0, y: -20}}
                            transition={{duration: 0.3, ease: "easeInOut"}}
                            style={{
                                position: 'absolute',
                                bottom: r.vmax(-150),
                                left: 0,
                                paddingLeft: r.vmax(50),
                                pointerEvents: 'none',
                                userSelect: 'none',
                            }}>
                            <div style={{
                                position: 'relative',
                                width: r.vmax(466),
                                height: r.vmax(100),
                                paddingLeft: r.vmax(107),
                                display: 'flex',
                                alignItems: 'center',
                                whiteSpace: 'pre-wrap',
                                color: 'white',
                                fontSize: r.vmax(24),
                                lineHeight: r.vmax(28)
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: r.vmax(-19),
                                    left: r.vmax(-35),
                                    width: r.vmax(121),
                                    height: r.vmax(134),
                                    zIndex: 1,
                                }}>
                                    {currentTask?.rewardIcon && (
                                        <TaskIcon icon={currentTask?.rewardIcon as Task["rewardIcon"]}/>
                                    )}
                                </div>
                                <span style={{
                                    position: 'relative',
                                    zIndex: 1,
                                }}>{`Награда: Суперсила\n«${currentTask?.reward}»`}</span>
                                <DisabledWrapper>
                                    <img alt="" src={rewardBg} style={{width: '100%', height: '100%'}}/>
                                </DisabledWrapper>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            <ValidationErrorModal
                isOpen={isErrorModalOpen}
                onClose={() => setIsErrorModalOpen(false)}
                errors={validationResult?.errors}
                errorCount={errorCount}
                hint={currentTask?.hint}
            />
        </>
    );
};

export default TaskPanel;
